import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyId } from '../hooks/useCompanyId';
import { useStrategyCalls } from '../hooks/useStrategyCalls';
import { useStrategyCallBooking } from '../hooks/useStrategyCallBooking';
import StrategyCallUpsellModal from '../components/portal/StrategyCallUpsellModal';
import { ensureCalendlyAssetsLoaded } from '../utils/calendlyWidget';
import { useResponsive } from '../contexts/ResponsiveContext';
import { getPillarScheduleLink, getPillarQuestionAssignee, getPillarQuestionAssigneeUser } from '../services/pillarsService';
import { createQuestion } from '../services/firebase/questions';
import OperationsTabNav from '../components/operations/OperationsTabNav';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const QuestionModal = ({ isOpen, onClose, onSubmit }) => {
  const [question, setQuestion] = useState('');
  const { darkMode } = useTheme();

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(question);
    setQuestion('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6 w-full max-w-md`}>
        <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>Ask a Question</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="question" className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Your Question
            </label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              rows="4"
              placeholder="Type your question here..."
              required
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const headerBtnBase =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm transition-shadow bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';

const PortalOperations = () => {
  const { isMobile, isTablet } = useResponsive();
  const { currentUser } = useAuth();
  const { companyId } = useCompanyId();

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [showCallUpsell, setShowCallUpsell] = useState(false);

  // Pillars settings
  const [operationsScheduleLink, setOperationsScheduleLink] = useState('');
  const [operationsQuestionAssignee, setOperationsQuestionAssignee] = useState('');
  const [userData, setUserData] = useState(null);

  // Shared Strategy Calls usage (company-wide Monthly 1:1)
  const { atLimit: strategyCallsAtLimit } = useStrategyCalls();

  const { openBooking, meetingAgendaModal } = useStrategyCallBooking({
    currentUser,
    userProfile: userData,
    disabled: strategyCallsAtLimit,
    variant: 'department',
    requireFocus: true,
  });

  useEffect(() => {
    ensureCalendlyAssetsLoaded();
  }, []);

  // Load pillars settings + user profile (for Calendly prefill)
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());

        const scheduleLink = await getPillarScheduleLink(currentUser.uid, 'operations');
        const questionAssignee = await getPillarQuestionAssignee(currentUser.uid, 'operations');
        setOperationsScheduleLink(scheduleLink);
        setOperationsQuestionAssignee(questionAssignee);
      } catch (error) {
        console.error('Error loading operations data:', error);
      }
    };
    loadData();
  }, [currentUser]);

  const handleQuestionSubmit = async (question) => {
    if (!currentUser) {
      alert('You must be logged in to submit a question.');
      return;
    }
    if (!question.trim()) {
      alert('Please enter your question.');
      return;
    }
    if (!operationsQuestionAssignee) {
      alert('No operations team member is assigned to handle questions. Please contact support.');
      return;
    }
    try {
      const assigneeUser = await getPillarQuestionAssigneeUser(currentUser.uid, 'operations');
      await createQuestion({
        accountId: companyId || currentUser.uid,
        accountName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        assigneeId: operationsQuestionAssignee,
        assigneeName: assigneeUser?.name || operationsQuestionAssignee,
        questionText: question.trim(),
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Unknown User',
        pillar: 'operations',
      });
      alert('Your question has been submitted successfully!');
    } catch (error) {
      console.error('Error submitting question:', error);
      alert('Failed to submit question. Please try again.');
    }
  };

  const openScheduleStrategyCall = () => {
    if (strategyCallsAtLimit) {
      setShowCallUpsell(true);
      return;
    }
    const baseUrl =
      operationsScheduleLink ||
      'https://calendly.com/daryl-yxbw/operations-strategy-call?hide_gdpr_banner=1';
    openBooking({ url: baseUrl });
  };

  return (
    <div className={`relative min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-8'}`}>
      <PortalBackground />
      <div className="relative">
      {/* Header: Schedule Call (left) · title · Ask Question (right) */}
      <div className={`relative mb-6 ${isMobile ? 'pt-10' : ''}`}>
        <button
          type="button"
          onClick={openScheduleStrategyCall}
          title="Schedule a Strategy Call"
          className={`absolute top-0 left-0 ${headerBtnBase} cursor-pointer hover:shadow-md text-gray-700 dark:text-gray-200`}
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">📞</span>
          {!isMobile && <span>Schedule Call</span>}
        </button>

        <div className="text-center">
          <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold tracking-tight text-gray-900 dark:text-white`}>Operations</h1>
        </div>

        <button
          type="button"
          onClick={() => setIsQuestionModalOpen(true)}
          title="Ask a Question"
          className={`absolute top-0 right-0 ${headerBtnBase} cursor-pointer hover:shadow-md text-gray-700 dark:text-gray-200`}
        >
          <span className="text-base leading-none shrink-0" aria-hidden="true">💬</span>
          {!isMobile && <span>Ask Question</span>}
        </button>
      </div>

      <OperationsTabNav />

      <div className="mb-8 min-h-[200px]">
        <Outlet context={{ embedded: true }} />
      </div>

      <QuestionModal
        isOpen={isQuestionModalOpen}
        onClose={() => setIsQuestionModalOpen(false)}
        onSubmit={handleQuestionSubmit}
      />
      {meetingAgendaModal}
      <StrategyCallUpsellModal
        isOpen={showCallUpsell}
        onClose={() => setShowCallUpsell(false)}
      />
      </div>
    </div>
  );
};

export default PortalOperations;
